/* global eeFormWidget */
import { useEffect, useRef } from 'react';

/* ExtraaEdge enquiry form widget.

   Their stock snippet (form-3/widget.js) is meant for static sites: it waits
   for DOMContentLoaded and calls init() once. In this SPA that runs on every
   route, and init() appends its own container to <body> when it can't find
   ours — which is how the form ended up under the footer on the home page.
   So we skip the snippet, load the library ourselves, and only call init()
   once the page that owns the container has rendered it. */

const BASE_URL = 'https://eeconfigstaticfiles.blob.core.windows.net/staticfiles/ee-form-widget/';
const CLIENT_ALIAS = 'sgadji';

let libraryPromise = null;

// The library declares `class eeFormWidget` at the top level of a classic
// script: that's a global binding, but not a property of `window`.
const isLibraryLoaded = () => typeof eeFormWidget === 'function';

const loadLibrary = () => {
    if (isLibraryLoaded()) return Promise.resolve();
    if (libraryPromise) return libraryPromise;

    window.ee_form_widget_baseurl = BASE_URL;

    if (!document.getElementById('__formWidgetCss')) {
        const css = document.createElement('link');
        css.id = '__formWidgetCss';
        css.rel = 'stylesheet';
        css.href = `${BASE_URL}css/stylesheet.min.css`;
        document.head.appendChild(css);
    }

    libraryPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = `${BASE_URL}js/eeFormWidget.min.js`;
        script.onload = resolve;
        script.onerror = () => {
            libraryPromise = null;
            script.remove();
            reject(new Error('Could not load the enquiry form widget'));
        };
        document.head.appendChild(script);
    });
    return libraryPromise;
};

/* Renders the widget form `formId` into the element with id `containerId`.
   The element must already be in the DOM when the component mounts. */
export const useEeFormWidget = (formId, containerId) => {
    // StrictMode runs effects twice in development; don't init the form twice.
    const started = useRef(false);

    useEffect(() => {
        if (started.current) return;
        started.current = true;

        loadLibrary()
            .then(() => {
                if (!document.getElementById(containerId)) return;
                return new eeFormWidget().init(CLIENT_ALIAS, formId, containerId);
            })
            .catch((err) => console.error(err));
    }, [formId, containerId]);
};
